"use client";

import { usePostData } from "@/app/hooks/query";
import { submitWireFile, UploadOrderFile } from "@/app/redux/orders/order-service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetClose, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Caption, Paragraph, Title } from "@/components/ui/typography";
import { Expand, FileCheck, Upload } from "@/lib/SystemIcons";
import { CircleCheck, File, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useShowSnackbar } from "@/app/hooks/useSnackbar";

import Image from "next/image";
import { Dialog, DialogClose, DialogContent, DialogDescription } from "@/components/ui/dialog";
import { useURLParams } from "@/app/hooks/useURLParams";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import orderService from "@/app/redux/order-management/order-management-service";
import { useDispatch } from "react-redux";
import { setOrderDetails } from "@/app/redux/order-management/order-management-slice";

const AttachRecipt = ({ paymentId, onClose, setIsReciptPresent, orderId }) => {
  const [recipt, setRecipt] = useState(null);
  const [success, setSuccess] = useState(false);
  const [location, setLocation] = useState(null);
  const { showSnackbar } = useShowSnackbar();
  const [isOpen, setIsOpen] = useState(false); // State to manage image modal visibility

  const fileUploadMutation = usePostData(UploadOrderFile);
  const submitWireFileMutation = usePostData(submitWireFile);
  const [ttCopy, setTtCopy] = useState("");
  const dispatch = useDispatch();

  const handleFileUpload = async file => {
    if (!file) {
      return;
    }

    const formData = new FormData();
    formData.append("image", file);

    // enqueueSnackbar('Please hold on, the image is being uploaded', { variant: 'warning', autoHideDuration: 2000 });

    fileUploadMutation.mutate(formData, {
      onSuccess: async resData => {
        try {
          if (resData) {
            setRecipt(file?.name);
            setLocation(resData?.data?.data?.location);
            showSnackbar(resData?.data?.message, "success");
          }
        } catch (error) {
          showSnackbar("Something Went Wrong", "error");
        }
      },
      onError: err => {
        showSnackbar("Something Went Wrong", "error");
      },
    });
  };
  const handleFileSubmit = () => {
    const payload = {
      payment_id: paymentId,
      document_url: location,
      tt_number: ttCopy,
    };

    if (location == null) {
      showSnackbar(" Please, upload a screenshot or image of your proof of transfer via wire transfer", "error");
      return;
    }

    submitWireFileMutation.mutate(payload, {
      onSuccess: async resData => {
        try {
          if (resData) {
            setSuccess(true);
            showSnackbar(resData?.data, "success");
            const response = await orderService.getOrderDetails(orderId);
            dispatch(setOrderDetails(response?.data[0]));
          }
        } catch (error) {
          showSnackbar("Something Went Wrong", "error");
        }
      },
      onError: err => {
        showSnackbar("Something Went Wrong", "error");
      },
    });
  };
  if (success) {
    return (
      <div className="relative h-[100%] flex flex-col justify-center items-center ">
        <Title size="lg" className="mb-4 text-center">
          Thank you for submitting your Wire Transfer receipt.
        </Title>
        <Caption className="mb-4 text-center">
          We'll review the details promptly. If verified, your order will be approved and marked as paid. If unverified,
          your order will remain pending for its remaining duration or until we confirm the payment with our bank,
          whichever comes first.
        </Caption>
        <Caption className="mb-4 text-center">We appreciate your patience during this process.</Caption>
        <Button variant="outline" onClick={onClose}>
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="relative h-[100%] ">
      <Title className="mb-3">Attach Your Receipt</Title>
      <Paragraph className="mb-4 text-midnightIndigo-300 ">
        Once funds have been transferred, please, upload a screenshot or image of your proof of transfer via wire
        transfer.
      </Paragraph>
      {recipt === null ? (
        <div className="h-[174px] relative bg-midnightIndigo-100 flex flex-col justify-center items-center cursor-pointer rounded mb-10">
          <input
            type="file"
            accept=".png,.jpg,.jpeg,.pdf"
            className="absolute inset-0 w-full h-full opacity-0"
            onChange={e => {
              const file = e.target.files[0];
              if (file) {
                handleFileUpload(file);
              }
            }}
          />
          <Upload />
          <p className="text-midnightIndigo-400">Drag the recipt here or click to upload</p>
          <Caption>PDF,PNG or JPG</Caption>
        </div>
      ) : (
        <div className="flex justify-between items-center h-[78px] bg-midnightIndigo-100 rounded mb-10 p-4">
          <div className="flex gap-1 items-center cursor-pointer" onClick={() => setIsOpen(true)}>
            <File className="h-[20px] w-[20px] mr-3" />
            <Paragraph>{recipt}</Paragraph>
            <CircleCheck className="ml-1 h-[20px] w-[20px] text-system-success" />
          </div>

          <div>
            <Button
              variant="icon"
              className="!h-[30px] !w-[30px] border p-1"
              onClick={() => {
                setRecipt(null);
                setLocation(null);
              }}
            >
              <Trash2 className="h-[20px] w-[20px]" />
            </Button>
          </div>
        </div>
      )}

      <Dialog open={isOpen} onOpenChange={() => setIsOpen(false)}>
        <DialogContent className="max-w-[60%] ">
          <DialogClose className="absolute right-0 top-0"></DialogClose>
          <DialogDescription>
            {recipt?.split(".").pop() === "pdf" ? (
              <iframe
                src={location}
                style={{ height: "500px" }}
                title="PDF Preview"
                className="max-h-[80vh] max-w-full mx-auto"
              />
            ) : (
              <Image
                height={1000}
                width={1000}
                src={location}
                alt="Receipt"
                className="max-h-[80vh] max-w-full mx-auto"
              />
            )}
          </DialogDescription>
        </DialogContent>
      </Dialog>
      <div className="mb-6">
        <Title className="mb-3">Enter TT copy</Title>
        <Caption className="text-midnightIndigo-300 ">
          Please, provide us with the TT copy. This helps us verify your transaction.
        </Caption>
      </div>
      <div>
        <Input placeholder="TT copy*" value={ttCopy} onChange={e => setTtCopy(e.target.value)} />
        <div className="flex flex-col justify-between gap-3 border-t mt-10 absolute bottom-[-20px] lg:relative lg:border-0 lg:flex-row left-0 w-full py-4 bg-white">
          <Button variant="outline" className="!w-full lg:!w-1/2" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" className="!w-full lg:!w-1/2  order-first lg:order-last" onClick={handleFileSubmit}>
            Submit receipt
          </Button>
        </div>
      </div>
    </div>
  );
};

const ViewRecipt = ({ viewRecipt, ttNumber, onClose }) => {
  const fileName = viewRecipt.match(/\d+-(.+)/)[1];
  const [isOpen, setIsOpen] = useState(false); // State to manage image modal visibility

  const handleImageClick = () => {
    setIsOpen(true);
  };

  const handleModalClose = () => {
    setIsOpen(false);
  };

  return (
    <div className="relative h-[100%] ">
      <Title className="mb-3">View Submitted Receipt</Title>
      <Paragraph className="mb-4 text-midnightIndigo-300 ">
        While your transfer receipt is undergoing validation, you can view your submission here.
      </Paragraph>

      <div className="flex justify-between items-center h-[78px] bg-midnightIndigo-100 rounded mb-10 p-4">
        <div className="flex gap-1 items-center">
          <File className="h-[20px] w-[20px] mr-3" />
          {/************************************PLEASE DISPLAY ONLY FILE NAME NOT WHOLE URL****************************************************************************** */}
          <Paragraph>{fileName}</Paragraph>
        </div>

        <div>
          <Button variant="icon" className="!h-[30px] !w-[30px] border p-1" onClick={handleImageClick}>
            <Expand />
          </Button>
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={handleModalClose}>
        <DialogContent className="max-w-[60%] ">
          <DialogClose className="absolute right-0 top-0"></DialogClose>
          <DialogDescription>
            {viewRecipt?.split(".").pop() === "pdf" ? (
              <iframe
                src={viewRecipt}
                style={{ height: "500px" }}
                title="PDF Preview"
                className="max-h-[80vh] max-w-full mx-auto"
              />
            ) : (
              <Image
                height={1000}
                width={1000}
                src={viewRecipt}
                alt="Receipt"
                className="max-h-[80vh] max-w-full mx-auto"
              />
            )}
          </DialogDescription>
        </DialogContent>
      </Dialog>

      <div className="mb-6">
        <Title className="mb-3">Enter TT copy</Title>
        <Caption className="text-midnightIndigo-300 ">
          Please, provide us with the TT copy. This helps us verify your transaction.
        </Caption>
      </div>
      <div>
        <Input placeholder="TT copy*" disabled value={ttNumber} />
        <div className="flex flex-col justify-between gap-3 border-t mt-10 absolute bottom-[-20px] lg:relative lg:border-0 lg:flex-row left-0 w-full  bg-white">
          <Button variant="outline" className="!w-full " onClick={onClose}>
            Go Back
          </Button>
        </div>
      </div>
    </div>
  );
};

export default function Recipt({ orderDetails }) {
  const routeParams = useURLParams();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const nextSearchParams = new URLSearchParams(searchParams.toString());

  const [isReciptPresent, setIsReciptPresent] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [open, setOpen] = useState(routeParams?.attachReciptModal == "true");

  const wireData = orderDetails?.Payments?.filter(ele => {
    return ele?.payment_method == "wire";
  });

  useEffect(() => {
    setIsReciptPresent(false);
    const wireData = orderDetails?.Payments?.filter(ele => {
      return ele?.payment_method == "wire";
    });
    if (wireData[0]?.WirePaymentDocuments && wireData[0]?.WirePaymentDocuments[0]?.document_url) {
      setIsReciptPresent(true);
    } else {
      setIsReciptPresent(false);
    }
  }, [orderDetails]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (routeParams?.attachReciptModal == "true") {
      setOpen(true);
    }
    if (open == false && routeParams?.attachReciptModal == "true") {
      nextSearchParams.delete("attachReciptModal");
      router.replace(`${pathname}?${nextSearchParams}`);
    }
  }, [routeParams]);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger>
        {showContent ? (
          isReciptPresent ? (
            <Button variant="outline" className="border-0 hover:bg-white">
              <FileCheck className="mr-2 -mt-1" />
              View Receipt
            </Button>
          ) : (
            <Button variant="primary" className="!w-[135px] ">
              Confirm Transfer
            </Button>
          )
        ) : (
          <Button variant="primary" loading="true" className="!w-[135px] "></Button>
        )}
      </SheetTrigger>
      <SheetContent
        hideClose
        onInteractOutside={e => e.preventDefault()}
        side="right"
        className=" w-full  md:w-[376px]  lg:w-[574px] !max-w-[574px]"
      >
        <SheetClose className="absolute left-[90%] z-10 cursor-pointer">
          <X role="button" className="text-midnightIndigo-300 h-6 w-6" />
        </SheetClose>
        {isReciptPresent ? (
          <ViewRecipt
            viewRecipt={wireData[0]?.WirePaymentDocuments[0]?.document_url}
            ttNumber={wireData[0]?.WirePaymentDocuments[0]?.tt_number}
            onClose={() => setOpen(false)}
          />
        ) : (
          <AttachRecipt
            paymentId={wireData[0]?.payment_id || wireData[0]?.id}
            onClose={() => setOpen(false)}
            setIsReciptPresent={setIsReciptPresent}
            orderId={orderDetails.id}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
